import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Box, Drawer, IconButton, useMediaQuery } from "@mui/material";
import { CssBaseline } from "@mui/material";
import {
  Menu as MenuIcon,
} from "@mui/icons-material";
import { CartProvider, useCart } from "./features/cart";
import { CartBarProvider, useCartBar } from "./features/cartBar";
import { CartPage } from "./features/cart";
import { CartBarPage } from "./features/cartBar";
// Providers
import { AuthProvider, useAuth, LanguageProvider, NotificationProvider, useNotification } from "./app/providers";
import { MessagingUnreadProvider } from "@/features/messaging";
import { NotificationsProvider } from "@/features/notifications";
import { useAutoMarkSectionNotificationsRead } from "@/features/notifications/hooks/useAutoMarkSectionNotificationsRead";
import { WebSocketProvider } from "@/shared/websocket";
import { WebThemeProvider } from "./contexts/WebThemeContext";
import { BuildingProvider } from "./contexts/BuildingContext";
import { AreaPermissionsProvider } from "@/contexts/AreaPermissionsContext";
import { SidebarNavProvider } from "@/contexts/SidebarNavContext";

// Layout Components
import DynamicSidebar from "@/components/Layout/DynamicSidebar";
import { SidebarCollapseHandle } from "@/components/Layout/SidebarCollapseHandle";
import ChatbotWidget from "@/components/Chatbot/ChatbotWidget";

// Components
import ProtectedRoute from "@/components/Auth/ProtectedRoute";
import FeatureRoute from "@/components/Auth/FeatureRoute";
import CooperativeURoute from "@/components/Auth/CooperativeURoute";
import { SidebarRouteGuard } from "@/components/Auth/SidebarRouteGuard";
import PublicRoute from "@/components/Auth/PublicRoute";
import { useTranslation } from "react-i18next";
import { RippleLoader } from "@/shared/ui";
const Dashboard = React.lazy(() => import("@/components/dashboard/Dashboard"));
const CalendarPage = React.lazy(() => import("@/pages/Calendar/CalendarPage"));
const Appointments = React.lazy(() =>
  import("./features/appointments").then((m) => ({ default: m.AppointmentsPage })),
);
const Reservations = React.lazy(() =>
  import("./features/reservations").then((m) => ({ default: m.ReservationsPage })),
);
const Reservation2Page = React.lazy(() =>
  import("./features/reservations").then((m) => ({ default: m.Reservation2Page })),
);
const ConciergePage = React.lazy(() =>
  import("@/features/conciergerie").then((m) => ({ default: m.ConciergePage })),
);
const LendingPage = React.lazy(() =>
  import("@/features/lending").then((m) => ({ default: m.LendingPage })),
);
const CooperativeUPressingEventsPage = React.lazy(
  () => import("@/features/events/pages/CooperativeUPressingEventsPage"),
);
const CooperativeULavageAutoEventsPage = React.lazy(
  () => import("@/features/events/pages/CooperativeULavageAutoEventsPage"),
);
const CooperativeUMedecineDouceEventsPage = React.lazy(
  () => import("@/features/events/pages/CooperativeUMedecineDouceEventsPage"),
);
const BarPage = React.lazy(() =>
  import("@/features/bar").then((m) => ({ default: m.BarPage })),
);
const Events = React.lazy(() =>
  import("@/features/events").then((m) => ({ default: m.EventsPage })),
);
const WidgetsPage = React.lazy(() =>
  import("@/features/widgets").then((m) => ({ default: m.WidgetsPage })),
);
const WidgetsNewsPage = React.lazy(() =>
  import("@/features/widgets").then((m) => ({ default: m.WidgetsNewsPage })),
);
const NotificationsPage = React.lazy(() =>
  import("@/features/notifications").then((m) => ({ default: m.NotificationsPage })),
);
const NotificationAreaPreferencesPage = React.lazy(() =>
  import("@/features/notifications").then((m) => ({
    default: m.NotificationAreaPreferencesPage,
  })),
);
const WidgetsWeatherPage = React.lazy(() =>
  import("@/features/widgets").then((m) => ({ default: m.WidgetsWeatherPage })),
);
const WidgetsTransportPage = React.lazy(() =>
  import("@/features/widgets").then((m) => ({ default: m.WidgetsTransportPage })),
);
const ConciergeHistoryPage = React.lazy(
  () => import("@/pages/Concierge/ConciergeHistoryPage"),
);
const BarHistoryPage = React.lazy(() => import("@/pages/Bar/BarHistoryPage"));
const OrdersHistoryPage = React.lazy(() => import("@/pages/Orders/OrdersHistoryPage"));
const GymPage = React.lazy(() => import("@/pages/Gym/GymPage"));
const BeautyPage = React.lazy(() => import("@/pages/Beauty/BeautyPage"));
const ServicesPage = React.lazy(() => import("@/pages/Services/ServicesPage"));
const ServiceReservationsPage = React.lazy(
  () => import("@/pages/Services/ServiceReservationsPage"),
);
const ProfilePage = React.lazy(() => import("@/pages/Profile/ProfilePage"));
const ExternalLinksPage = React.lazy(() => import("@/pages/ExternalLinks/ExternalLinksPage"));
const MessageriePage = React.lazy(() =>
  import("@/features/messaging").then((m) => ({ default: m.MessageriePage })),
);
const TicketingLayout = React.lazy(() => import("@/pages/Ticketing/TicketingLayout"));
const TicketingReportFormPage = React.lazy(() => import("@/pages/Ticketing/TicketingReportFormPage"));
const TicketingAllTicketsPage = React.lazy(() => import("@/pages/Ticketing/TicketingAllTicketsPage"));
const TicketingAssignmentsPage = React.lazy(() => import("@/pages/Ticketing/TicketingAssignmentsPage"));
const ResetPasswordPage = React.lazy(() => import("@/pages/ResetPassword/ResetPasswordPage"));
const ConfirmResetPage = React.lazy(() => import("@/pages/ResetPassword/ConfirmResetPage"));
const AccountDeactivatedPage = React.lazy(
  () => import("@/pages/Account/AccountDeactivatedPage"),
);
const ReactivateAccountPage = React.lazy(
  () => import("@/pages/Account/ReactivateAccountPage"),
);
const Login = React.lazy(() => import("@/pages/Login/Login"));
const Register = React.lazy(() => import("@/pages/Register/Register"));
const RegisterQRPage = React.lazy(() =>
  import("@/pages/RegisterQR").then((m) => ({ default: m.RegisterQRPage })),
);
const RegisterGatePage = React.lazy(() =>
  import("@/pages/RegisterQR").then((m) => ({ default: m.RegisterGatePage })),
);
const InviteAcceptPage = React.lazy(() => import("@/pages/Invite/InviteAcceptPage"));
const CooperativeURegister = React.lazy(() => import("@/pages/Register/CooperativeURegister"));
const NotFound = React.lazy(() => import("@/pages/NotFound/NotFound"));
const UnloggedBeautyEventsPage = React.lazy(() =>
  import("@/pages/UnloggedEvents").then((m) => ({ default: m.UnloggedBeautyEventsPage })),
);
const UnloggedConferenceEventsPage = React.lazy(() =>
  import("@/pages/UnloggedEvents").then((m) => ({ default: m.UnloggedConferenceEventsPage })),
);
const UnloggedSportEventsPage = React.lazy(() =>
  import("@/pages/UnloggedEvents").then((m) => ({ default: m.UnloggedSportEventsPage })),
);
const UnloggedMeetingReservationsPage = React.lazy(() =>
  import("@/pages/UnloggedReservations").then((m) => ({ default: m.UnloggedMeetingReservationsPage })),
);
const UnloggedDNAPage = React.lazy(() => import("@/pages/UnloggedDNA/UnloggedDNAPage"));

// Responsive component that shows timeline on large screens, reunion page on small screens
// Events wrapper component to use translations
const EventsWrapper: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Events 
      title={t("events.title", "Events")} 
      subtitle={t("events.subtitle", "Discover and register for upcoming events")} 
      eventType={{ type: "Conference" }} 
    />
  );
};

const ResponsiveMeetings: React.FC = () => {
  const isDesktop = useMediaQuery("(min-width:900px)");

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      {isDesktop ? <Reservation2Page /> : <Reservations />}
    </Box>
  );
};

const RouteFallback: React.FC = () => (
  <RippleLoader
    fullScreen
    title="Chargement..."
    subtitle="Chargement de la page"
    size="medium"
  />
);
// import { AuthTest } from "./components/AuthTest"; // Commented out to simplify

// Login Page Component

// Main Layout Component
function MainLayoutNotificationSync() {
  useAutoMarkSectionNotificationsRead();
  return null;
}

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isShortViewport = useMediaQuery("(max-height:860px)");
  const isTightViewport = useMediaQuery("(max-width:1200px)");
  const isDesktopNav = useMediaQuery("(min-width:900px)");
  const compactLayout = isShortViewport || isTightViewport;
  const SIDEBAR_DEFAULT_WIDTH = compactLayout ? 210 : 300;
  const SIDEBAR_COLLAPSED_WIDTH = 74;
  const sidebarWidth = SIDEBAR_DEFAULT_WIDTH;
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const desktopSidebarWidth = sidebarHidden
    ? SIDEBAR_COLLAPSED_WIDTH
    : sidebarWidth;
  const { logout } = useAuth();
  const { showTranslatedNotification } = useNotification();
  const { clear: clearConciergeCart } = useCart();
  const { clear: clearBarCart } = useCartBar();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const raw = localStorage.getItem("main-layout-sidebar-hidden");
    setSidebarHidden(raw === "true");
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleDesktopSidebar = () => {
    const nextHidden = !sidebarHidden;
    setSidebarHidden(nextHidden);
    localStorage.setItem("main-layout-sidebar-hidden", String(nextHidden));
  };

  const handleLogout = async () => {
    try {
      await logout();
      clearConciergeCart();
      clearBarCart();
      showTranslatedNotification(
        "notifications.logoutSuccess",
        "success",
        3000
      );
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      showTranslatedNotification("notifications.logoutError", "error", 5000);
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const sidebarContent = (
    <DynamicSidebar
      onLogout={handleLogout}
      onNavigate={handleNavigate}
      currentPath={location.pathname}
      onToggleSidebar={toggleDesktopSidebar}
      collapsed={sidebarHidden}
    />
  );

  return (
    <NotificationsProvider>
    <MainLayoutNotificationSync />
    <MessagingUnreadProvider>
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          display: { xs: "inline-flex", md: "none" },
          position: "fixed",
          top: 8,
          left: 8,
          zIndex: (theme) => theme.zIndex.drawer + 3,
          bgcolor: "background.default",
          color: "text.primary",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: 1,
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
        size="small"
        aria-label="Ouvrir la navigation"
      >
        <MenuIcon />
      </IconButton>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: `${desktopSidebarWidth}px` },
          flexShrink: { md: 0 },
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
          overflow: "hidden",
        }}
      >
        {/* Mobile drawer — single sidebar instance (not duplicated with desktop) */}
        {!isDesktopNav ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            zIndex: (theme) => theme.zIndex.drawer + 2,
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: { xs: compactLayout ? 200 : 220, sm: compactLayout ? 210 : 240 },
              top: { xs: 56, sm: 64, md: 0 },
              height: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)", md: "100vh" },
            },
          }}
        >
          {sidebarContent}
        </Drawer>
        ) : (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            width: { md: sidebarHidden ? 0 : `${sidebarWidth}px` },
            flexShrink: { md: 0 },
            "& .MuiDrawer-root": {
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
            },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: { md: `${desktopSidebarWidth}px` },
              top: 0,
              height: "100vh",
              overflowX: "hidden",
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>
        )}
      </Box>
      <SidebarCollapseHandle
        collapsed={sidebarHidden}
        sidebarWidth={desktopSidebarWidth}
        onToggle={toggleDesktopSidebar}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${desktopSidebarWidth}px)` },
          mt: { xs: 7, sm: 8, md: 0 },
          height: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)", md: "100vh" },
          maxHeight: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)", md: "100vh" },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: compactLayout ? { xs: 0.5, sm: 1, md: 1.25 } : { xs: 1, sm: 2, md: 3 },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <SidebarRouteGuard>{children}</SidebarRouteGuard>
        </Box>
      </Box>
      <ChatbotWidget />
    </Box>
    </MessagingUnreadProvider>
    </NotificationsProvider>
  );
};

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppComplete() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
          <AuthProvider>
            <WebThemeProvider>
              <NotificationProvider>
              <CssBaseline />
              <BuildingProvider>
                <WebSocketProvider>
                <CartProvider>
                  <CartBarProvider>
                    <Router>
                      <AreaPermissionsProvider>
                      <SidebarNavProvider>
                      <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        {/* Public Routes */}
                        <Route
                          path="/MultiAppEvenement"
                          element={<UnloggedConferenceEventsPage />}
                        />
                        <Route
                          path="/MultiAppSport"
                          element={<UnloggedSportEventsPage />}
                        />
                        <Route
                          path="/MultiAppBeauty"
                          element={<UnloggedBeautyEventsPage />}
                        />
                        <Route
                          path="/MultiappReservation"
                          element={<UnloggedMeetingReservationsPage />}
                        />
                        <Route path="/dna" element={<UnloggedDNAPage />} />
                        <Route
                          path="/login"
                          element={
                            <PublicRoute>
                              <Login />
                            </PublicRoute>
                          }
                        />
                        <Route path="/register/gate" element={<RegisterGatePage />} />
                        <Route path="/register" element={<RegisterQRPage />} />
                        <Route
                          path="/register/*"
                          element={
                            <PublicRoute>
                              <Register />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/invite/:token"
                          element={
                            <PublicRoute>
                              <InviteAcceptPage />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/cooperativeU/register"
                          element={
                            <PublicRoute>
                              <CooperativeURegister />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/reset-password"
                          element={
                            <PublicRoute>
                              <ResetPasswordPage />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/confirmReset/:uid/:token"
                          element={
                            <PublicRoute>
                              <ConfirmResetPage />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/confirmReset"
                          element={
                            <PublicRoute>
                              <ConfirmResetPage />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/account-deactivated"
                          element={
                            <PublicRoute>
                              <AccountDeactivatedPage />
                            </PublicRoute>
                          }
                        />
                        <Route
                          path="/reactivate/:userPk/:code"
                          element={
                            <PublicRoute>
                              <ReactivateAccountPage />
                            </PublicRoute>
                          }
                        />

                        {/* Protected Routes */}
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <CalendarPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <Dashboard />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/widgets"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <WidgetsPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/widgets/news"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <WidgetsNewsPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <NotificationsPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/widgets/weather"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <WidgetsWeatherPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/widgets/transport"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <WidgetsTransportPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/appointments"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="appointments">
                                  <Appointments />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/calendar"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <CalendarPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/meetings"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="meetings">
                                  <ResponsiveMeetings />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/concierge"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="concierge">
                                  <ConciergePage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/lending"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="lending">
                                  <LendingPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cooperativeU/events/pressing"
                          element={
                            <ProtectedRoute>
                              <CooperativeURoute>
                                <MainLayout>
                                  <FeatureRoute feature="events">
                                    <CooperativeUPressingEventsPage />
                                  </FeatureRoute>
                                </MainLayout>
                              </CooperativeURoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cooperativeU/events/lavage-auto"
                          element={
                            <ProtectedRoute>
                              <CooperativeURoute>
                                <MainLayout>
                                  <FeatureRoute feature="events">
                                    <CooperativeULavageAutoEventsPage />
                                  </FeatureRoute>
                                </MainLayout>
                              </CooperativeURoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cooperativeU/events/medecine-douce"
                          element={
                            <ProtectedRoute>
                              <CooperativeURoute>
                                <MainLayout>
                                  <FeatureRoute feature="events">
                                    <CooperativeUMedecineDouceEventsPage />
                                  </FeatureRoute>
                                </MainLayout>
                              </CooperativeURoute>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/concierge/history"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <ConciergeHistoryPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/bar"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="bar">
                                  <BarPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/bar/history"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="barOrders">
                                  <BarHistoryPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/orders"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <OrdersHistoryPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cart"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <CartPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cartBar"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <CartBarPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/events"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="events">
                                  <EventsWrapper />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/gym"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="gym">
                                  <GymPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/beauty"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="beauty">
                                  <BeautyPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/services/reservations"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="services">
                                  <ServiceReservationsPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/services"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="services">
                                  <ServicesPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ticketing"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="ticketing">
                                  <TicketingLayout />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<Navigate to="report" replace />} />
                          <Route path="report" element={<TicketingReportFormPage />} />
                          <Route path="all" element={<TicketingAllTicketsPage />} />
                          <Route path="assignments" element={<TicketingAssignmentsPage />} />
                        </Route>
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <ProfilePage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile/notification-settings"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <NotificationAreaPreferencesPage />
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/external-links"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="externalLinks">
                                  <ExternalLinksPage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/messagerie"
                          element={
                            <ProtectedRoute>
                              <MainLayout>
                                <FeatureRoute feature="messaging">
                                  <MessageriePage />
                                </FeatureRoute>
                              </MainLayout>
                            </ProtectedRoute>
                          }
                        />
                        {/* Auth Test Route */}
                        {/* Calendar Route - Commented out to simplify app */}
                        {/* <Route
                    path="/calendar"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <CalendarView />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  /> */}
                        {/* Auth Test Route - Commented out to simplify */}
                        {/* <Route
                    path="/auth-test"
                    element={
                      <ProtectedRoute>
                        <MainLayout>
                          <AuthTest />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  /> */}

                        {/* 404 */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                      </Suspense>
                      </SidebarNavProvider>
                      </AreaPermissionsProvider>
                    </Router>
                  </CartBarProvider>
                </CartProvider>
                </WebSocketProvider>
              </BuildingProvider>
              </NotificationProvider>
            </WebThemeProvider>
          </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default AppComplete;
